import { NovatiqAuthorizationClient } from '@/_auth-client';
import { IAccessInfo } from '@/_types/models/authorization/access-info.model';
import { ROUTES } from '@/_constants';
import { PasswordExpiryResetHelper } from '@/_helpers';
import { AuthorizationService } from '@/features/authorization/data/authorization.service';
import { AppStorage } from '@/_storage';
import { NovatiqMessageService } from '@/layouts/service/message.service';
import { NovatiqAutoFocusDirective } from '@/shared/directive/novatiq-autofocus.directive';
import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { debounceTime, delay, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-novatiq-verify-2fa',
  templateUrl: './novatiq-verify-2fa.component.html',
  styleUrls: ['./novatiq-verify-2fa.component.scss'],
  host: {
    class: 'block w-full'
  },
  imports: [ReactiveFormsModule, FormsModule, TranslateModule, ButtonModule, NovatiqAutoFocusDirective]
})
export class NovatiqVerify2FAComponent implements OnInit {
  apiError: string;
  verify2faFormGroup: FormGroup;
  pageSettings: unknown;
  showSuccessMessage: boolean;
  fromExpiredPasswordFlow = false;
  hasShowError = false;

  @Output() onverify = new EventEmitter<Event>();

  private readonly authorizationService = inject(AuthorizationService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly translate = inject(TranslateService);
  private readonly authorizationClient = inject(NovatiqAuthorizationClient);
  private readonly messageService = inject(NovatiqMessageService);
  private readonly appStorage = inject(AppStorage);
  private readonly passwordExpiryResetHelper = inject(PasswordExpiryResetHelper);

  get formControls() {
    return this.verify2faFormGroup.controls;
  }

  ngOnInit(): void {
    this.pageSettings = this.route.snapshot.data.pageSettings;
    this.initializeProperties();
  }

  cancel() {
    this.messageService.clearMessage();
    this.authorizationClient.removeUserSession();
    this.router.navigate([ROUTES.LOGIN.path]);
  }

  onSubmit() {
    if (this.verify2faFormGroup.invalid) {
      return false;
    }

    this.fromExpiredPasswordFlow = this.passwordExpiryResetHelper.checkFromExpiredPasswordFlow() === 'true';
    const originalToken = this.authorizationClient.accessToken;
    const resetTokenData = this.passwordExpiryResetHelper.getResetTokenData();

    if (this.fromExpiredPasswordFlow) {
      const authToken = resetTokenData.authToken;
      if (authToken) {
        const token = {
          accessToken: resetTokenData.authToken,
          expiresIn: 30,
          require2FA: resetTokenData.require2FA === 'true',
          setupComplete2FA: false
        };
        this.authorizationClient.addToken(token);
      }
    }

    this.authorizationService
      .verify2FA(this.verify2faFormGroup.value.TFAcode)
      .pipe(
        switchMap((data) => {
          this.onverify.emit(data);
          this.showSuccessMessage = true;
          return this.authorizationService.getUserAccessInfo();
        }),
        tap((userAccessInfo: IAccessInfo[]) => {
          if (!this.fromExpiredPasswordFlow) {
            this.authorizationClient.addAccess(userAccessInfo);
          }
        }),
        delay(2000)
      )
      .subscribe({
        next: () => {
          if (this.fromExpiredPasswordFlow && originalToken != this.authorizationClient.accessToken) {
            this.authorizationClient.removeUserSession();
          }

          if (this.fromExpiredPasswordFlow) {
            const username = resetTokenData.expiredUsername;
            const resetToken = resetTokenData.resetToken;

            this.router.navigate([ROUTES.CREATE_PASSWORD.path], {
              queryParams: {
                username: username,
                token: resetToken,
                fromExpired: true
              }
            });
          } else {
            this.router.navigate([ROUTES.LOGIN.path]);
          }
        },
        error: () => {
          this.messageService.sendMessage(this.translate.instant('invalid2fa'), 'error');
        }
      });
  }

  close() {
    this.hasShowError = false;
  }

  private initializeProperties() {
    this.showSuccessMessage = false;
    this.verify2faFormGroup = this.formBuilder.group({
      TFAcode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });

    this.verify2faFormGroup
      .get('TFAcode')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        if (this.verify2faFormGroup.valid) {
          this.onSubmit();
        }
      });
  }
}
